import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, ChevronDown, MessageCircle } from 'lucide-react';
import { companyInfo, servicesList } from '../data/mock';
import { Button } from './ui/button';
import TextLink from './TextLink';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import SchemaMarkup from './SchemaMarkup';
import Footer from './Footer';

const Layout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col">
      <SchemaMarkup />
      
      {/* Top Bar */}
      <div className="bg-neutral-900 text-white py-2 px-4 text-sm hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 hover:text-amber-400 transition-colors">
              <Phone className="w-4 h-4" />
              {companyInfo.phone}
            </a>
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {companyInfo.addressFull}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-amber-400 font-medium">Free Estimates Available</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'}`}>
        <nav className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src={companyInfo.logoLight} 
                alt="Gray Contracting Logo" 
                className="h-12 md:h-16 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" className="nav-link text-sm font-medium text-neutral-700 hover:text-amber-600">Home</Link>
              <Link to="/about" className="nav-link text-sm font-medium text-neutral-700 hover:text-amber-600">About</Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-sm font-medium text-neutral-700 hover:text-amber-600">
                    Services
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 bg-white">
                  <DropdownMenuItem asChild>
                    <Link to="/services" className="w-full font-medium">All Services</Link>
                  </DropdownMenuItem>
                  {servicesList.map((service) => (
                    <DropdownMenuItem key={service.id} asChild>
                      <Link to={`/services/${service.id}`} className="w-full">{service.shortTitle}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/gallery" className="nav-link text-sm font-medium text-neutral-700 hover:text-amber-600">Gallery</Link>
              <Link to="/blog" className="nav-link text-sm font-medium text-neutral-700 hover:text-amber-600">Blog</Link>
              <Link to="/contact" className="nav-link text-sm font-medium text-neutral-700 hover:text-amber-600">Contact</Link>
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-1 text-sm font-semibold text-neutral-800 hover:text-amber-600 transition-colors">
                <Phone className="w-4 h-4" />
                Call
              </a>
              <TextLink className="flex items-center gap-1 text-sm font-semibold text-neutral-800 hover:text-amber-600 transition-colors">
                <MessageCircle className="w-4 h-4" />
                Text
              </TextLink>
              <Link to="/contact">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6">
                  Free Estimate
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-neutral-800"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-neutral-200 pt-4 animate-fadeIn">
              <div className="flex flex-col gap-4">
                <Link to="/" className="py-2 text-base font-medium text-neutral-700">Home</Link>
                <Link to="/about" className="py-2 text-base font-medium text-neutral-700">About</Link>
                <div>
                  <Link to="/services" className="py-2 text-base font-medium text-neutral-700 block">Services</Link>
                  <div className="pl-4 mt-2 flex flex-col gap-2">
                    {servicesList.map((service) => (
                      <Link key={service.id} to={`/services/${service.id}`} className="text-sm text-neutral-600 py-1">{service.shortTitle}</Link>
                    ))}
                  </div>
                </div>
                <Link to="/gallery" className="py-2 text-base font-medium text-neutral-700">Gallery</Link>
                <Link to="/blog" className="py-2 text-base font-medium text-neutral-700">Blog</Link>
                <Link to="/contact" className="py-2 text-base font-medium text-neutral-700">Contact</Link>
                <div className="pt-4 border-t border-neutral-200">
                  <div className="flex items-center gap-4 mb-4">
                    <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-amber-600 font-semibold">
                      <Phone className="w-5 h-5" />
                      Call
                    </a>
                    <TextLink className="flex items-center gap-2 text-amber-600 font-semibold">
                      <MessageCircle className="w-5 h-5" />
                      Text
                    </TextLink>
                  </div>
                  <Link to="/contact">
                    <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold">
                      Get Free Estimate
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
};

export default Layout;
